import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook para lazy loading de listas
 * @param {Array} items - Array de elementos a mostrar
 * @param {number} initialItems - Número inicial de elementos a mostrar
 * @param {number} itemsPerLoad - Número de elementos a cargar cada vez
 * @returns {Object} { visibleItems, loadMore, hasMore, loadingRef }
 */
export const useLazyLoad = (items = [], initialItems = 12, itemsPerLoad = 12) => {
  const [visibleCount, setVisibleCount] = useState(initialItems);
  const loadingRef = useRef(null);
  const observerRef = useRef(null);
  const itemsRef = useRef(items);
  const visibleCountRef = useRef(initialItems);
  const loadMoreRef = useRef(null);

  // Mantener referencias actualizadas
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    visibleCountRef.current = visibleCount;
  }, [visibleCount]);

  const visibleItems = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  const loadMore = useCallback(() => {
    setVisibleCount(prev => {
      const currentItems = itemsRef.current;
      const maxItems = currentItems.length;
      const nextCount = Math.min(prev + itemsPerLoad, maxItems);
      return nextCount;
    });
  }, [itemsPerLoad]);

  // Guardar referencia de loadMore
  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  // Reset cuando cambian los items (nueva lista)
  useEffect(() => {
    setVisibleCount(initialItems);
    visibleCountRef.current = initialItems;
  }, [items.length, initialItems]);

  // Callback ref para configurar el observer cuando el elemento esté disponible
  const setLoadingRef = useCallback((node) => {
    // Limpiar observer anterior
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // Limpiar referencia anterior
    if (loadingRef.current && loadingRef.current !== node) {
      loadingRef.current = null;
    }

    loadingRef.current = node;

    if (!node || !hasMore) {
      return;
    }

    // Crear observer cuando el elemento esté disponible
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          const currentCount = visibleCountRef.current;
          const currentItems = itemsRef.current;
          if (currentCount < currentItems.length && loadMoreRef.current) {
            loadMoreRef.current();
          }
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      }
    );

    observerRef.current = observer;
    observer.observe(node);
  }, [hasMore]);

  // Limpiar observer cuando no hay más elementos
  useEffect(() => {
    if (!hasMore && observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, [hasMore]);

  return {
    visibleItems,
    loadMore,
    hasMore,
    loadingRef: setLoadingRef
  };
};

export default useLazyLoad;
